import { SystemOptionType } from 'data/types';
import { formatCoverageScore } from 'utils/coverage-score-format';
import {
  CoverageShortfall,
  getCoverageShortfalls
} from 'utils/coverage-shortfalls';
import { ductedPerformanceFromSystem } from 'utils/ducted-performance-display';
import {
  getSystemCoolingMaxTemp,
  getSystemHeatingMinTemp
} from './equipment-comparison-utils';

// What one coverage block shows: the headline percent, the caption that
// finishes its sentence, an optional second caption line, and the tooltip
// that explains what the percent measures. The ducted display model is
// structurally this shape, so it passes straight through.
export type CoverageSide = {
  value: string;
  caption: string;
  detail: string | null;
  tooltip?: string;
  shortfalls?: CoverageShortfall[];
};

export type CoverageSides = {
  heating: CoverageSide;
  cooling: CoverageSide;
};

// Ducted systems get the design-load headline with the hours-weighted
// coverage underneath; ductless strings keep their scores and the outdoor
// temperatures they were rated through.
export function coverageSidesForSystem(
  system: SystemOptionType
): CoverageSides {
  const ducted = ductedPerformanceFromSystem(system);
  if (ducted) {
    return ducted;
  }
  const heatingMin = getSystemHeatingMinTemp(system);
  const coolingMax = getSystemCoolingMaxTemp(system);
  return {
    heating: {
      value: formatCoverageScore(system.heatingScore),
      caption: heatingMin !== null ? `Down to ${heatingMin}°F` : 'Down to —°F',
      detail: null,
      shortfalls: getCoverageShortfalls(
        'heating',
        system.heatingScore,
        system.condenserCoverage
      )
    },
    cooling: {
      value: formatCoverageScore(system.coolingScore),
      caption: coolingMax !== null ? `Up to ${coolingMax}°F` : 'Up to —°F',
      detail: null,
      shortfalls: getCoverageShortfalls(
        'cooling',
        system.coolingScore,
        system.condenserCoverage
      )
    }
  };
}
